import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Eraser, Maximize, Minimize, X } from "lucide-react";

interface ChatHeaderProps {
  onClearChat: () => void;
  onToggleFullScreen: () => void;
  isFullScreen: boolean;
  onClose?: () => void;
}

export const ChatHeader = ({ onClearChat, onToggleFullScreen, isFullScreen, onClose }: ChatHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm">
          <img src="/img/NewDiggerIcon.png" alt="" className="h-5 w-5 brightness-0 invert" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold leading-tight text-foreground">{t("chat.title")}</span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            {t("chat.status")}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClearChat}
              aria-label={t("chat.clear")}
              className="h-7 w-7 rounded-sm text-muted-foreground hover:text-foreground"
            >
              <Eraser className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("chat.clear")}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleFullScreen}
              aria-label={isFullScreen ? t("chat.exitFullscreen") : t("chat.fullscreen")}
              className="h-7 w-7 rounded-sm text-muted-foreground hover:text-foreground"
            >
              {isFullScreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isFullScreen ? t("chat.exitFullscreen") : t("chat.fullscreen")}</TooltipContent>
        </Tooltip>
        {onClose && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label={t("chat.close")}
                className="h-7 w-7 rounded-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("chat.close")}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
